import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, ArrowRight, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";

const ease = [0.16, 1, 0.3, 1] as const;

const content = {
  en: {
    seoTitle: "Compliance Reality Check — EastWestIT",
    seoDescription:
      "A pragmatic assessment that tests whether your Microsoft 365 security and AI governance controls actually work — not just whether they exist on paper.",
    lead: "Your organisation passed the audit. But would your controls survive an incident?",
    intro:
      "Most compliance frameworks prove intent. They don't prove resilience. This assessment tests whether your security controls, AI governance, and data boundaries actually function — not just whether they exist in a policy document.",
    cta: "Book a scoping conversation",
    findLabel: "What we typically find",
    findTitle: "The gap between paper and practice",
    find: [
      "Permissions that passed audit but expose sensitive data to Copilot",
      "Sharing links that predate your current security policy",
      "Compliance controls that exist in policy but aren't enforced technically",
      "Identity lifecycle gaps — leavers with active access, orphaned accounts",
      "AI governance frameworks that exist on paper but have no operational teeth",
      "Board reporting that shows green when the reality is amber",
    ],
    getLabel: "Deliverables",
    getTitle: "What you walk away with",
    get: [
      "Validated control effectiveness report — not a checkbox matrix",
      "Gap map between audit posture and operational reality",
      "Prioritised remediation roadmap with effort/impact scoring",
      "Board-ready executive summary with honest risk language",
      "30-day follow-up validation of critical findings",
    ],
    notLabel: "Honest positioning",
    notTitle: "This is not",
    not: [
      "A tick-box audit that tells you what you want to hear",
      "A 200-page report that nobody reads",
      "Generic best-practice recommendations from a template",
      "A sales pitch disguised as an assessment",
    ],
    finalTitle: "Ready to find out what's real?",
    finalText:
      "A 30-minute scoping call to understand your environment, compliance posture, and what you need validated. No pitch. No obligation.",
    rights: "All rights reserved.",
  },
  da: {
    seoTitle: "Compliance Reality Check — test om jeres sikkerhed virker | EastWestIT",
    seoDescription:
      "En pragmatisk vurdering, der tester om dine Microsoft 365-sikkerhedskontroller og din AI-governance faktisk virker — ikke bare om de findes på papiret.",
    lead: "Jeres organisation bestod audit. Men ville jeres kontroller holde under en reel hændelse?",
    intro:
      "De fleste compliance-rammeværk dokumenterer intention. De dokumenterer ikke robusthed. Denne vurdering tester, om jeres sikkerhedskontroller, AI-governance og datagrænser rent faktisk fungerer — ikke bare om de står i en politik.",
    cta: "Book en afklarende samtale",
    findLabel: "Det finder jeg typisk",
    findTitle: "Beskrevne processer stemmer ikke med virkeligheden",
    find: [
      "Rettigheder, der bestod audit, men eksponerer følsomme data for Copilot",
      "Delingslinks, der er ældre end jeres nuværende sikkerhedspolitik",
      "Compliance-kontroller, der findes i politikken, men ikke håndhæves teknisk",
      "Huller i identitetslivscyklussen — fratrådte med aktiv adgang og forældreløse konti",
      "AI-governance, der findes på papiret, men ikke har nogen operationel effekt",
      "Bestyrelsesrapportering, der viser grønt, når virkeligheden er gul",
    ],
    getLabel: "Leverancer",
    getTitle: "Det får I med jer",
    get: [
      "Valideret rapport over kontrollernes effektivitet — ikke en afkrydsningsmatrix",
      "Gap-kort mellem audit-status og den operationelle virkelighed",
      "Prioriteret handlingsplan med vurdering af indsats og effekt",
      "Ledelsesresumé klar til bestyrelsen med ærligt risikosprog",
      "Opfølgende validering af kritiske fund efter 30 dage",
    ],
    notLabel: "Ærlig positionering",
    notTitle: "Det her er ikke",
    not: [
      "En afkrydsnings-audit, der fortæller jer det, I gerne vil høre",
      "En rapport på 200 sider, som ingen læser",
      "Generiske best practice-anbefalinger fra en skabelon",
      "Et salgsmøde forklædt som en vurdering",
    ],
    finalTitle: "Klar til at finde ud af, hvad der er virkeligt?",
    finalText:
      "En afklarende samtale på 30 minutter, hvor jeg forstår jeres miljø, compliance-status og hvad der skal valideres. Intet salg. Ingen forpligtelser.",
    rights: "Alle rettigheder forbeholdes.",
  },
} as const;

const ComplianceRealityCheck = () => {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const c = content[lang === "da" ? "da" : "en"];

  const scrollToContact = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  const list = (items: readonly string[], variant: "find" | "get" | "not") => (
    <div className="space-y-4">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: i * 0.08, ease }}
          className={`flex items-start gap-4 bg-card border rounded-lg px-5 py-4 ${
            variant === "not" ? "border-destructive/20" : "border-border"
          }`}
        >
          {variant === "find" && <AlertTriangle size={18} className="text-primary shrink-0 mt-0.5" />}
          {variant === "get" && (
            <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--success))" }} />
          )}
          {variant === "not" && <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />}
          <span className="text-foreground font-body text-sm leading-relaxed">{item}</span>
        </motion.div>
      ))}
    </div>
  );

  const heading = (label: string, title: string) => (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease }}
    >
      <p className="label-text mb-3">{label}</p>
      <h2 className="font-display font-bold text-foreground mb-10">{title}</h2>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO title={c.seoTitle} description={c.seoDescription} path="/compliance-reality-check" lang={lang} />
      <Navbar />

      <section className="pt-32 pb-20 relative overflow-hidden">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-[0.06]"
          style={{ background: "radial-gradient(circle, hsl(24 94% 53%), transparent 70%)" }}
        />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 mb-8">
              <AlertTriangle size={14} className="text-primary" />
              <span className="text-sm text-primary font-medium font-body">{t.services.flagship.badge}</span>
            </div>
            <h1 className="font-display font-bold text-foreground mb-6">Compliance Reality Check</h1>
            <p className="text-xl text-muted-foreground leading-relaxed font-body max-w-2xl mb-4">{c.lead}</p>
            <p className="text-muted-foreground leading-relaxed font-body max-w-2xl mb-10">{c.intro}</p>
            <Button size="lg" variant="premium" onClick={scrollToContact}>
              {c.cta}
              <ArrowRight size={18} />
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" style={{ background: "var(--gradient-subtle)" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {heading(c.findLabel, c.findTitle)}
            {list(c.find, "find")}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {heading(c.getLabel, c.getTitle)}
            {list(c.get, "get")}
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: "var(--gradient-subtle)" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {heading(c.notLabel, c.notTitle)}
            {list(c.not, "not")}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-display font-bold text-foreground mb-6">{c.finalTitle}</h2>
            <p className="text-muted-foreground font-body leading-relaxed mb-10">{c.finalText}</p>
            <Button size="lg" variant="premium" onClick={scrollToContact}>
              {c.cta}
              <ArrowRight size={18} />
            </Button>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground font-body">
            © {new Date().getFullYear()} EastWest IT (DK43969803). {c.rights}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ComplianceRealityCheck;
